/**
 * PatientPackagesPanel.tsx
 *
 * Patient-detail packages section.
 * Table layout matching legacy package-list.php contract (Title, Description, Date).
 * Row click expands to show item-level Use/Return controls (V5 operational extension).
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPatientPackages,
  type PatientPackage,
} from '@/services/packages.service';
import { AddPackageModal } from './AddPackageModal';
import { PackageDetailModal } from './PackageDetailModal';
import { Button } from '@/components/ui/Button';
import { descriptionToText } from '@/lib/utils';


function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface PatientPackagesPanelProps {
  patientId: number;
  patientName?: string;
}

// ─── PatientPackagesPanel ─────────────────────────────────────────────────────

export default function PatientPackagesPanel({ patientId, patientName }: PatientPackagesPanelProps) {
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<PatientPackage | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const queryClient = useQueryClient();

  const { data: packages, isLoading, isError } = useQuery({
    queryKey: ['patient-packages', patientId, showInactive],
    queryFn: () => getPatientPackages(patientId, { includeInactive: showInactive || undefined }),
    staleTime: 30_000,
  });

  function handleEnrolled() {
    void queryClient.invalidateQueries({ queryKey: ['patient-packages', patientId] });
  }

  return (
    <div className="space-y-4">
      {/*
        SCRUM-1415. Same shape as the Forms header: an unwrapped row where the only
        compressible item is the "Show Inactive" label, sitting next to a `shrink-0`
        Button — so the label wrapped and the button was pushed against the screen edge
        with no right margin.
      */}
      <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Packages</h2>
        <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none max-md:shrink-0 max-md:whitespace-nowrap">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Show Inactive
          </label>
          <Button
            onClick={() => setEnrollOpen(true)}
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Package
          </Button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-slate-400">Loading packages…</p>}

      {isError && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Failed to load packages.
        </div>
      )}

      {!isLoading && !isError && packages?.length === 0 && (
        <p className="py-4 text-sm text-slate-400 italic">No packages found.</p>
      )}

      {packages && packages.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Date</th>
                {showInactive && <th className="px-4 py-3">Status</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {packages.map((pkg) => (
                <tr
                  key={pkg.patientPackageId}
                  className={`cursor-pointer ${pkg.active ? 'hover:bg-slate-50' : 'bg-red-50/50 hover:bg-red-50'}`}
                  onClick={() => setSelectedPkg(pkg)}
                >
                  <td className={`px-4 py-3 font-medium ${pkg.active ? 'text-slate-800' : 'text-red-700'}`}>{pkg.packageName}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-[300px] truncate" title={descriptionToText(pkg.notes) || undefined}>
                    {descriptionToText(pkg.notes) || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{formatShortDate(pkg.purchaseDate)}</td>
                  {showInactive && (
                    <td className="px-4 py-3">
                      {pkg.declined ? (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Declined</span>
                      ) : !pkg.active ? (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Inactive</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Active</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddPackageModal
        patientId={patientId}
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        onSaved={() => {
          setEnrollOpen(false);
          handleEnrolled();
        }}
      />

      <PackageDetailModal
        pkg={selectedPkg}
        patientId={patientId}
        patientName={patientName}
        open={!!selectedPkg}
        onClose={() => setSelectedPkg(null)}
      />
    </div>
  );
}
